package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import com.qrkontaq.data.dto.WebNameCountDto
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import kotlin.math.ln

private var cachedWorldSvg: String? = null

/**
 * Choropleth of visitor countries over an inline world SVG (@svg-maps/world,
 * lowercase ISO-2 path ids). The file is fetched once and cached in module
 * state; countries are tinted emerald on a log intensity scale with a native
 * SVG <title> hover.
 */
@Composable
internal fun QpWorldMap(countries: List<WebNameCountDto>) {
    if (countries.isEmpty()) {
        SpanText(PlatformT.noData, Modifier.fontSize(13.px).color(Color.rgba(242, 247, 244, 0.55f)))
        return
    }
    val max = countries.maxOf { it.count }.coerceAtLeast(1)
    val byIso = countries.associate { it.name.lowercase() to it.count }

    Box(Modifier.fillMaxWidth().attrsModifier { classes("qp-map") }) {
        Div(attrs = {
            ref { host ->
                val paint = { svg: String ->
                    host.innerHTML = svg
                    (host.firstElementChild as? HTMLElement)
                        ?.setAttribute("style", "width:100%;height:auto;display:block")
                    tint(host, byIso, max)
                }
                val cached = cachedWorldSvg
                if (cached != null) {
                    paint(cached)
                } else {
                    window.fetch("/platform/world.svg")
                        .then { it.text() }
                        .then { text ->
                            cachedWorldSvg = text
                            paint(text)
                            null
                        }
                }
                onDispose { host.innerHTML = "" }
            }
        })
    }
}

private fun tint(
    host: Element,
    byIso: Map<String, Long>,
    max: Long,
) {
    byIso.forEach { (iso, count) ->
        val path = host.querySelector("[id=\"$iso\"]") as? HTMLElement ?: return@forEach
        val intensity = 0.25 + 0.75 * ln(1.0 + count) / ln(1.0 + max)
        path.style.setProperty("fill", "rgba(52,211,153,$intensity)")
        if (path.querySelector("title") == null) {
            val nice = path.getAttribute("name") ?: countryName(iso.uppercase())
            val t = window.document.createElementNS("http://www.w3.org/2000/svg", "title")
            t.textContent = "$nice · $count"
            path.appendChild(t)
        }
    }
}
